import { useQuery, gql } from '@apollo/client';
import { Link } from 'react-router-dom';

// --- GraphQL Query ---
const GET_ALL_SCHOOL_CLASSES = gql`
    query GetAllSchoolClasses {
        allSchoolClasses {
            id
            name
            senior
            teacher {
                id
                surname
                lastname
            }
            students {
                id
            }
        }
    }
`;

// --- Empty State Component ---
function NoClassesFound() {
    return (
        <div className="text-center py-16">
            <h3 className="text-lg font-medium text-gray-800">
                Keine Klassen gefunden
            </h3>
            <p className="mt-1 text-sm text-gray-500">
                Es gibt keine Klassen in deiner Schule. Bitte erstelle eine neue Klasse, um fortzufahren.
            </p>
            <div className="mt-6">
                <Link
                    to="/admin/classes/create"
                    className="mainBtn inline-flex items-center text-white px-6 py-2"
                >
                    <svg className="-ml-1 mr-2 h-5 w-5" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                        <path fillRule="evenodd" d="M10 3a1 1 0 011 1v5h5a1 1 0 110 2h-5v5a1 1 0 11-2 0v-5H4a1 1 0 110-2h5V4a1 1 0 011-1z" clipRule="evenodd" />
                    </svg>
                    Klasse erstellen
                </Link>
            </div>
        </div>
    );
}

export default function ClassManagement() {
    const { loading, error, data } = useQuery(GET_ALL_SCHOOL_CLASSES, {
        fetchPolicy: 'cache-and-network',
        errorPolicy: 'all'
    });

    if (loading) {
        return <div className="py-8 text-center">Loading classes...</div>;
    }
    if (error) {
        return <div className="py-8 text-center text-red-600">Error loading classes: {error.message}</div>;
    }
    if (!data || data.allSchoolClasses.length === 0) {
        return <NoClassesFound />;
    }

    return (
        // The component now has no outer padding or margins.
        <div className="w-full">
                    <header className="mb-6 flex flex-col sm:flex-row justify-between sm:items-center gap-4">
                        <h1 className="text-3xl font-bold text-neutral-900">
                            Klassenübersicht
                        </h1>
                        <Link
                            to="/admin/classes/create"
                            className="mainBtn inline-flex items-center justify-center sm:w-auto w-full text-white px-5 py-2"
                        >
                            <svg className="mr-2 h-5 w-5" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                                <path fillRule="evenodd" d="M10 3a1 1 0 011 1v5h5a1 1 0 110 2h-5v5a1 1 0 11-2 0v-5H4a1 1 0 110-2h5V4a1 1 0 011-1z" clipRule="evenodd" />
                            </svg>
                            <span>Klasse erstellen</span>
                        </Link>
                    </header>
                        <main>
                            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-5 gap-6">
                                {data.allSchoolClasses.map((schoolClass) => (
                                    <Link
                                        to={`/admin/classes/${schoolClass.id}`}
                                        key={schoolClass.id}
                                        className="group wrap block bg-white p-6 rounded-xl border-2 border-neutral-200 transition-all duration-100 hover:border-neutral-300"
                                    >
                                        <div className="flex justify-between items-start">
                                            <h2 className="text-xl font-bold text-neutral-800 truncate" title={schoolClass.name}>
                                                {schoolClass.name}
                                            </h2>
                                            <span className={`px-3 py-1 text-xs font-semibold rounded-full ${
                                                schoolClass.senior ? 'bg-indigo-100 text-indigo-800' : 'bg-teal-100 text-teal-800'
                                            }`}>
                                                {schoolClass.senior ? 'Oberstufe' : 'Unterstufe'}
                                            </span>
                                        </div>

                                <div className="mt-4 pt-4 border-t-2 border-neutral-100 space-y-3">
                                <p className="flex items-center text-neutral-600">
                                    <svg className="h-5 w-5 mr-3 text-neutral-400 flex-shrink-0" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                                    </svg>
                                    <span>
                                        <span className="uppercase font-medium mr-1">{schoolClass.teacher.lastname}</span>
                                        {schoolClass.teacher.surname}
                                    </span>
                                </p>
                                {/* Student Info */}
                                <p className="flex items-center text-neutral-600">
                                     <svg className="h-5 w-5 mr-3 text-neutral-400 flex-shrink-0" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z" />
                                    </svg>
                                    <span>{schoolClass.students.length} Schüler</span>
                                </p>
                            </div>
                        </Link>
                    ))}
                </div>
            </main>
        </div>
    );
}